import React from 'react';

/**
 * Minimal user shape accepted by UserAvatar. The codebase uses this pattern
 * with both `UserProfile` (from `@/types/team`) and lighter ad-hoc user
 * objects (e.g. Firestore snapshot shapes in onboarding), so we accept
 * anything that has `photoURL` and `displayName`.
 */
export interface UserAvatarUser {
    photoURL?: string | null;
    displayName?: string | null;
    uid?: string;
}

export interface UserAvatarProps {
    user: UserAvatarUser | null | undefined;
    /** Fallback character when `displayName` is missing. Defaults to 'U'. */
    fallback?: string;
    /** Alt text override. Defaults to `user.displayName || 'User'`. */
    alt?: string;
    /** Extra classes applied to the `<img>` element. */
    imgClassName?: string;
}

/**
 * Shared avatar content: renders `<img>` if `user.photoURL` is present,
 * otherwise the first character of `displayName` (or `fallback`).
 *
 * Call sites provide their own wrapper (so they keep control of
 * size/background/shape) and drop `<UserAvatar user={u} />` inside it.
 * Replaces the repeated `{photoURL ? <img .../> : initial}` snippet.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
    user,
    fallback = 'U',
    alt,
    imgClassName = 'w-full h-full object-cover',
}) => {
    if (user?.photoURL) {
        return (
            <img
                src={user.photoURL}
                alt={alt ?? user.displayName ?? 'User'}
                className={imgClassName}
                loading="lazy"
                onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                }}
            />
        );
    }
    return <>{user?.displayName?.[0] || fallback}</>;
};

export default UserAvatar;
